import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { paths } from '../../paths';
import { useProfile } from '../context/ProfileContext';
import Header from '../header/AppHeader';
import ProfileCard from '../profile/ProfileCard';
import background from "../../assets/dashboard-bg.jpg";

const TAG_CLS = {
  Activation: "bg-[#D15B28] text-white",
  Update: "bg-black/[0.07] text-[#2B1B17]/55",
  Info: "bg-black/[0.07] text-[#2B1B17]/55",
};

const STATUS_BADGE = {
  approved: { label: "APPROVED", cls: "bg-green-100 text-green-700" },
  submitted: { label: "IN REVIEW", cls: "bg-black/[0.06] text-black/50" },
  pending: { label: "PENDING", cls: "bg-[#D15B28] text-white" },
  rejected: { label: "REJECTED", cls: "bg-red-100 text-red-600" },
};

const STATUS_SUBLINE = {
  approved: "Approved · points awarded",
  submitted: "Submitted, awaiting admin",
  pending: "Pending submission",
  rejected: "Rejected · resubmit",
};

const timeAgo = (date) => {
  if (!date) return "";
  const diff = Date.now() - new Date(date).getTime();
  if (Number.isNaN(diff)) return "";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

// Wave of dots (under Total Points) — count lit up reflects % of available points earned
const WaveDots = ({ percent = 0 }) => {
  const heights = [10, 7, 5, 8, 12, 16, 11, 6, 4];
  const filled = Math.round((clamp(percent, 0, 100) / 100) * heights.length);
  return (
    <div className="flex items-end gap-2 h-6">
      {heights.map((y, i) => (
        <span
          key={i}
          className={`w-1.5 h-1.5 rounded-full ${i >= heights.length - filled ? "bg-[#D15B28]" : "bg-black/20"}`}
          style={{ marginBottom: `${y * 0.7}px` }}
        />
      ))}
    </div>
  );
};

// Line graph (under Current Rank) — final point height reflects real rank percentile
const LineGraph = ({ percent = 0 }) => {
  const p = clamp(percent, 0, 100) / 100;
  const yEnd = (26 - p * 21).toFixed(1);
  const yMid = ((15 + Number(yEnd)) / 2).toFixed(1);
  return (
    <svg viewBox="0 0 140 30" className="w-full h-7" preserveAspectRatio="none">
      <polyline points="0,22 20,20 40,22 60,18 80,19 95,15" fill="none" stroke="#000" strokeOpacity="0.18" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points={`95,15 112,${yMid} 130,${yEnd}`} fill="none" stroke="#D15B28" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="130" cy={yEnd} r="3" fill="#D15B28" />
    </svg>
  );
};

// Arc graph (under Tasks Completed) — crossover point reflects real completion %
const ArcGraph = ({ percent = 0 }) => {
  const p = clamp(percent, 0, 100) / 100;
  const x = (20 + p * 100).toFixed(1);
  const y = (22 - p * 14).toFixed(1);
  return (
    <svg viewBox="0 0 140 30" className="w-full h-7" preserveAspectRatio="none">
      <polyline points={`2,24 ${x},${y}`} fill="none" stroke="#D15B28" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points={`${x},${y} 138,24`} fill="none" stroke="#000" strokeOpacity="0.18" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x} cy={y} r="3" fill="#D15B28" />
    </svg>
  );
};

// Mini bar chart (under Registrations) — bars lit up reflect real referral count
const BarChart = ({ filled = 0 }) => {
  const bars = [6, 8, 7, 10, 9, 14, 18, 16, 22, 12];
  const litCount = clamp(filled, 0, bars.length);
  return (
    <div className="flex items-end gap-1 h-6">
      {bars.map((h, i) => (
        <span
          key={i}
          className={`w-2 rounded-sm ${i < litCount ? "bg-[#D15B28]" : "bg-black/15"}`}
          style={{ height: `${h}px` }}
        />
      ))}
    </div>
  );
};

const StatCard = ({ label, value, sub, chart }) => (
  <div className="bg-[#F5EFEB]/90 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-white/20 flex flex-col justify-between h-full overflow-hidden font-montserrat">
    <div>
      <p className="text-[10px] font-extrabold tracking-widest text-[#2B1B17]/50 uppercase font-montserrat">{label}</p>
      <p className="text-4xl font-black text-[#2B1B17] leading-none mt-1 font-montserrat">{value}</p>
    </div>
    <div>
      <p className="text-[11px] text-[#2B1B17]/50 font-medium mt-1 font-montserrat">{sub}</p>
      {chart && <div className="mt-2">{chart}</div>}
    </div>
  </div>
);

const Dashboard = () => {
  const { data, loading, error, refresh } = useProfile();

  useEffect(() => {
    if (!data) refresh();
  }, [data, refresh]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#0E0705] text-[#F8F5F2] flex items-center justify-center font-sans">
        <div className="text-lg opacity-70 animate-pulse">Loading your dashboard...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-[#0E0705] text-[#F8F5F2] flex flex-col items-center justify-center p-4 font-sans">
        <p className="text-red-400 mb-4 font-medium">Unable to load your dashboard. Please try again.</p>
        <button
          onClick={refresh}
          className="bg-[#D15B28] text-white px-6 py-2 rounded-full font-semibold transition hover:bg-[#b04a1f]"
        >
          Retry
        </button>
      </div>
    );
  }

  const progress = data?.progress || {};
  const announcements = data?.announcements || [];
  const tasks = data?.recentSubmissions || [];
  const totalPoints = data?.totalPoints || 0;
  const totalAvailablePoints = data?.totalAvailablePoints || 0;
  const pointsPercent = totalAvailablePoints > 0 ? (totalPoints / totalAvailablePoints) * 100 : 0;
  const referralCount = data?.referralCount ?? 0;

  return (
    <div
      className="min-h-[100dvh] lg:h-[100dvh] w-full overflow-y-auto lg:overflow-hidden bg-center bg-cover bg-no-repeat text-[#2B1B17] font-sans antialiased flex flex-col"
      style={{ backgroundImage: `url(${background})` }}
    >
      <Header activeTab="Dashboard" />

      {/* --- HERO SECTION --- */}
      <section className="px-6 md:px-12 pt-4 pb-3 max-w-[1600px] mx-auto w-full flex-shrink-0">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 relative z-10">

          <div className="text-white flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5 max-w-3xl">
            <h1 className="text-[70px] font-barlow font-black tracking-tight drop-shadow-sm leading-none">
              Dashboard
            </h1>
            <p className="text-sm md:text-base font-montserrat text-white/90 leading-relaxed font-normal drop-shadow-sm sm:max-w-[40rem]">
              With the vision to bring out the leader in you, IIT Guwahati Spirit presents the Campus Ambassador Program.
            </p>
          </div>

          {/* PROFILE CARD */}
          <ProfileCard showReferral />

        </div>
      </section>

      {/* --- MAIN GRID --- */}
      <main className="max-w-[1600px] mx-auto px-2 md:px-2 w-full relative z-20 flex-1 min-h-0 pb-2">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1fr)] gap-2 items-stretch lg:h-full">

          {/* LEFT: 2x2 STAT CARDS */}
          <div className="grid grid-cols-2 grid-rows-2 gap-2 lg:h-full">
            <StatCard
              label="Total Points"
              value={totalPoints.toLocaleString()}
              sub="across tasks & referrals"
              chart={<WaveDots percent={pointsPercent} />}
            />
            <StatCard
              label="Current Rank"
              value={`#${data?.currentRank ?? "—"}`}
              sub={`out of ${progress.totalCAs ?? 0} CAs`}
              chart={<LineGraph percent={progress.rankPercentile ?? 0} />}
            />
            <StatCard
              label="Tasks Completed"
              value={progress.tasksCompleted ?? 0}
              sub={`${progress.tasksCompleted ?? 0} of ${progress.totalTasks ?? 0} · ${progress.tasksPending ?? 0} pending`}
              chart={<ArcGraph percent={progress.completionPercentage ?? 0} />}
            />
            <StatCard
              label="Registrations"
              value={referralCount}
              sub="via referral link, live"
              chart={<BarChart filled={referralCount} />}
            />
          </div>

          {/* MIDDLE: YOUR TASKS */}
          <div className="bg-[#F5EFEB]/90 backdrop-blur-md rounded-2xl p-5 md:p-6 shadow-xl border border-white/20 flex flex-col lg:min-h-0 overflow-hidden font-montserrat">
            <div className="flex justify-between items-start mb-3 flex-shrink-0">
              <div>
                <h2 className="text-sm font-black tracking-widest text-[#2B1B17]/60 uppercase">Your Tasks</h2>
                <p className="text-xs text-black/40 font-medium mt-0.5">
                  {tasks.length} {tasks.length === 1 ? "submission" : "submissions"}
                </p>
              </div>
              <Link to={paths.task} className="text-xs font-semibold text-[#2B1B17]/55 hover:text-[#2B1B17]">View All →</Link>
            </div>

            <div className="lg:overflow-y-auto flex-1 lg:min-h-0 divide-y divide-black/[0.04]">
              {tasks.length === 0 && (
                <p className="text-sm text-black/40 py-8 text-center">No task activity yet.</p>
              )}
              {tasks.map((task) => {
                const badge = STATUS_BADGE[task.status] || { label: task.status?.toUpperCase() || "—", cls: "bg-black/[0.06] text-black/50" };
                return (
                  <div key={task.id} className="flex items-center justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <p className="font-bold text-[#2B1B17] truncate">{task.taskName || "Task"}</p>
                      <p className="text-xs text-black/40 mt-0.5">{STATUS_SUBLINE[task.status] || ""}</p>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className="font-black text-[#2B1B17] whitespace-nowrap">+{task.taskPoints ?? 0} pts</span>
                      <span className={`text-[10px] font-extrabold tracking-wider px-2.5 py-1 rounded-full uppercase ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: ANNOUNCEMENTS */}
          <div className="bg-[#F5EFEB]/90 backdrop-blur-md rounded-2xl p-5 md:p-6 shadow-xl border border-white/20 flex flex-col lg:min-h-0 overflow-hidden font-montserrat">
            <div className="mb-3 flex-shrink-0">
              <h2 className="text-sm font-black tracking-widest text-[#2B1B17]/60 uppercase">Announcements</h2>
              <p className="text-xs text-black/40 font-medium mt-0.5">
                {announcements.length} {announcements.length === 1 ? "update" : "updates"}
              </p>
            </div>

            <div className="lg:overflow-y-auto flex-1 lg:min-h-0 flex flex-col gap-4">
              {announcements.length === 0 && (
                <p className="text-sm text-black/40 py-8 text-center">No announcements right now.</p>
              )}
              {announcements.map((a) => (
                <div key={a.id} className="border-b border-black/[0.05] last:border-none pb-3 last:pb-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[10px] font-extrabold tracking-wider px-2.5 py-1 rounded-full uppercase ${TAG_CLS[a.tag] || TAG_CLS.Info}`}>
                      {a.tag || "Info"}
                    </span>
                    <span className="text-[11px] text-black/35 font-medium whitespace-nowrap">{timeAgo(a.createdAt)}</span>
                  </div>
                  <h4 className="font-bold text-[#2B1B17] leading-snug mt-1.5">{a.title}</h4>
                  {a.body && <p className="text-xs text-black/50 mt-1 leading-relaxed">{a.body}</p>}
                </div>
              ))}
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Dashboard;